import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught', error, info);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return <FallbackUI onReset={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}

function FallbackUI({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className="min-h-dvh flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="max-w-sm w-full bg-white dark:bg-slate-900 border border-rose-300 dark:border-rose-700 rounded-lg p-4 sm:p-6 text-center space-y-3">
        <h1 className="text-lg font-semibold text-rose-700 dark:text-rose-300">{t('errorBoundary.title')}</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">{t('errorBoundary.message')}</p>
        <div className="flex gap-2 justify-center">
          <button
            type="button"
            onClick={onReset}
            className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 text-sm font-medium px-3 py-1.5"
          >
            {t('errorBoundary.tryAgain')}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 text-sm font-medium px-3 py-1.5"
          >
            {t('errorBoundary.reload')}
          </button>
        </div>
      </div>
    </div>
  );
}
